<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：営業所マスタ画面
 *
 * 作成者：2021/11/23 ノア）金
 * 最終修正者：2022/1/11 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.salesOfficeMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.salesOfficeMaster.contentTitle') }}</span>
          <span>{{ tableData.length }}</span>
          <span class="subject">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :rules="rules" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          highlight-current-row
          @current-change="handleCurrentChange"
          @selection-change="handleSelectionChange"
          :row-class-name="tableRowClassName"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- 営業所コード -->
          <el-table-column
            class-name="required"
            prop="businessOfficeCode"
            :label="$t('master.salesOfficeMaster.salesOfficeCode')"
            width="150"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].businessOfficeCode'"
                :rules="rules.businessOfficeCode"
              >
                <el-input
                  :id="'tableData[' + scope.$index + '].businessOfficeCode'"
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.businessOfficeCode"
                  @change="format(scope.row)"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 営業所名 -->
          <el-table-column
            class-name="required"
            prop="businessOfficeName"
            :label="$t('master.salesOfficeMaster.salesOfficeName')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].businessOfficeName'"
                :rules="rules.businessOfficeName"
              >
                <el-input
                  :id="'tableData[' + scope.$index + '].businessOfficeName'"
                  v-model.trim="scope.row.businessOfficeName"
                  size="small"
                  @change="onRepeatItemChange(formRef, 'businessOfficeName')"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- エリア -->
          <el-table-column prop="area" :label="$t('master.salesOfficeMaster.area')" width="150" :sortable="true">
            <template slot-scope="scope">
              <el-form-item>
                <el-select v-model="scope.row.areaId" size="small" clearable @change="onAreaChange(scope.row)">
                  <el-option
                    v-for="item in areaAllocationMasterData"
                    :key="item.id"
                    :label="item.areaName"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 都道府県 -->
          <el-table-column
            prop="prefectures"
            :label="$t('master.salesOfficeMaster.prefectures')"
            width="150"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item>
                <el-select v-model="scope.row.prefectureId" clearable size="small">
                  <el-option
                    v-for="item in getPrefecturesByAreaId(scope.row.areaId)"
                    :key="item.id"
                    :label="item.prefectureName"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 住所 -->
          <el-table-column
            class-name="required"
            prop="location"
            :label="$t('master.salesOfficeMaster.address')"
            width="350"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].location'" :rules="rules.location">
                <el-input
                  v-model.trim="scope.row.location"
                  size="small"
                  :id="'tableData[' + scope.$index + '].location'"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 連絡先（TEL） -->
          <el-table-column prop="tel" :label="$t('master.salesOfficeMaster.tel')" width="200" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].tel'" :rules="rules.tel">
                <el-input
                  v-model.trim="scope.row.tel"
                  size="small"
                  :id="'tableData[' + scope.$index + '].tel'"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- メールアドレス -->
          <el-table-column prop="mail" :label="$t('master.salesOfficeMaster.email')" width="300" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].mail'" :rules="rules.mail">
                <el-input
                  v-model.trim="scope.row.mail"
                  size="small"
                  :id="'tableData[' + scope.$index + '].mail'"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
        <!-- 地図 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onMapButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.map)"
        >
          <tti-svg-icon icon-name="wo_002"></tti-svg-icon>
          {{ $t('buttons.map') }}
        </el-button>
        <!-- テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onTamplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
        >
          {{ $t('buttons.template') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 入力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onInputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.input') }}
        </el-button>
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>
    <location-info @onGetAdress="onGetAdress"></location-info>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'
import { find } from 'lodash'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import LocationInfo from '@/components/LocationInfo.vue'
import { i18n } from '@/locales'
import { common, dialog } from '@/store/modules'
import {
  getBusinessOfficeMasterData,
  updateBusinessOfficeMasterData,
  businessOfficeMasterCSVDownload,
  getAreaMasterDataWhoHasSubPrefecture
} from '@/store/apis'
import { BusinessOfficeMasterData, AreaMasterData, PrefectureMasterData, MasterData } from '@/models'
import { DataType, TemplateCode, MasterType } from '@/commons/constants'
import { RULES, VALID_PATTERN } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import { localService } from '@/commons/services/localService'

/**
 * 営業所マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-sales-ofc',
  components: {
    LocationInfo
  }
})
export default class MST_SALES_OFC extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_SALES_OFC'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'businessOfficeCode'

  /**
   * 画面名
   */
  pageName = i18n.t('master.salesOfficeMaster.viewTitle').toString()

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = i18n.t('master.csvHeader.MST_SALES_OFC').toString()

  /**
   * テンプレートコード
   */
  templateCode = TemplateCode.MST_SALES_OFC

  /**
   * エリア割当リスト
   */
  areaAllocationMasterData: AreaMasterData[] = []

  /**
   * 管理対象マスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, BusinessOfficeMasterData[]>

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = ['businessOfficeCode', 'businessOfficeName', 'area', 'prefectures', 'location', 'tel', 'mail']

  /**
   * 最大件数をチェックするか
   */
  checkMaxCount = true

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // 営業所コード
      businessOfficeCode: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.salesOfficeMaster.salesOfficeCode')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeCode').toString()],
          trigger: 'blur'
        },
        // 半角数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberWithLength', [i18n.t('master.salesOfficeMaster.salesOfficeCode'), 8])
            .toString(),
          csvMessage: 'vaildate.csvFile.number',
          messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeCode').toString(), 8],
          trigger: 'blur'
        },
        {
          validRule: [
            // 8文字
            {
              rule: RULES.RANGE,
              range: [8, 8],
              message: i18n
                .t('vaildate.common.numberWithLength', [i18n.t('master.salesOfficeMaster.salesOfficeCode'), 8])
                .toString(),
              csvMessage: 'vaildate.csvFile.numberWithLength',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeCode').toString(), 8]
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'businessOfficeCode',
              message: i18n
                .t('vaildate.common.repeat', [i18n.t('master.salesOfficeMaster.salesOfficeCode')])
                .toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeCode').toString()]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 営業所名
      businessOfficeName: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.salesOfficeMaster.salesOfficeName')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeName').toString()],
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.salesOfficeMaster.salesOfficeName'), 50])
                .toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeName').toString(), 50]
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'businessOfficeName',
              message: i18n
                .t('vaildate.common.repeat', [i18n.t('master.salesOfficeMaster.salesOfficeName')])
                .toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.salesOfficeName').toString()]
            }
          ],
          trigger: 'blur'
        }
      ],
      // エリア
      area: [
        // コード変換
        {
          validRule: [
            {
              rule: RULES.CODECHANGE,
              csvMessage: 'vaildate.csvFile.noMasterData',
              messageEnterPoint: [
                i18n.t('master.salesOfficeMaster.area').toString(),
                i18n.t('master.salesOfficeMaster.area').toString()
              ],
              masterData: this.areaAllocationMasterData,
              masterLayoutName: 'areaName'
            }
          ]
        }
      ],
      // 都道府県
      prefectures: [
        // コード変換
        {
          validRule: [
            {
              rule: RULES.CODECHANGE,
              csvMessage: 'vaildate.csvFile.noMasterData',
              messageEnterPoint: [
                i18n.t('master.salesOfficeMaster.prefectures').toString(),
                i18n.t('master.salesOfficeMaster.prefecturesAlloc').toString()
              ],
              masterData: this.getPrefectureList(this.areaAllocationMasterData),
              masterLayoutName: 'prefectureName'
            }
          ]
        },
        // 関連マスタ
        {
          validRule: [
            {
              rule: RULES.INPARENT,
              csvMessage: 'vaildate.csvFile.childNotInParent',
              messageEnterPoint: [
                i18n.t('master.salesOfficeMaster.prefectures').toString(),
                i18n.t('master.salesOfficeMaster.area').toString()
              ],
              masterData: this.areaAllocationMasterData,
              masterParentLayoutName: 'areaName',
              masterParentKey: 'area',
              masterLayoutName: 'prefectureName',
              childrenListKey: 'prefectureList',
              masterKey: 'prefectureId'
            }
          ]
        }
      ],
      // 住所
      location: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.salesOfficeMaster.address')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.salesOfficeMaster.address').toString()],
          trigger: 'blur'
        },
        // 50文字
        {
          validRule: [
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.salesOfficeMaster.address'), 50]).toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.address').toString(), 50]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 連絡先（TEL）
      tel: [
        // TEL
        {
          pattern: VALID_PATTERN[RULES.TEL],
          message: i18n
            .t('vaildate.common.alphanumericAndSymbols', [i18n.t('master.salesOfficeMaster.tel')])
            .toString(),
          csvMessage: 'vaildate.csvFile.tel',
          messageEnterPoint: [i18n.t('master.salesOfficeMaster.tel').toString()],
          trigger: 'blur'
        },
        // 16文字
        {
          validRule: [
            {
              rule: RULES.MAXLENGTH,
              number: 16,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.salesOfficeMaster.tel'), 16]).toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.tel').toString(), 16]
            }
          ],
          trigger: 'blur'
        }
      ],
      // メールアドレス
      mail: [
        // mail
        {
          pattern: VALID_PATTERN[RULES.EMAIL],
          message: i18n.t('vaildate.common.invalidMail').toString(),
          csvMessage: 'vaildate.csvFile.email',
          messageEnterPoint: [i18n.t('master.salesOfficeMaster.email').toString()],
          trigger: 'blur'
        },
        // 50文字
        {
          validRule: [
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.salesOfficeMaster.email'), 50]).toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.salesOfficeMaster.email').toString(), 50]
            }
          ],
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_SALES_OFC > mounted')
    this.initFormTemplate()
    await this.fetchAreaAllocationMasterData()
    this.fetchTableData()
    this.checkPageLock()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return () => {
      const param = {
        type: MasterType.DETAIL
      }
      return getBusinessOfficeMasterData(param)
    }
  }

  /**
   * 8ビット未満自動補正0と重複チェックある項目を変更した後に実行する。
   *
   * @param {BusinessOfficeMasterData} val 営業所マスタ
   * @returns {void}
   */
  format(val: BusinessOfficeMasterData): void {
    val.businessOfficeCode = this.prefixZero(val.businessOfficeCode as string)
    this.onRepeatItemChange(this.formRef, 'businessOfficeCode')
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCsvRule(): any {
    return this.rules
  }

  /**
   * エリア割当マスタを取得する
   *
   * @returns {void}
   */
  async fetchAreaAllocationMasterData(): Promise<void> {
    try {
      const master = await getAreaMasterDataWhoHasSubPrefecture()
      this.areaAllocationMasterData = (master.areaList as AreaMasterData[]) || []
    } catch (e) {
      this.$log.debug('MST_SALES_OFC > fetchAreaAllocationMasterData: e=', e)
      throw e
    }
  }

  /**
   * エリアを変更した時の処理
   *
   * @param {BusinessOfficeMasterData} row 変更した行
   * @returns {void}
   */
  onAreaChange(row: BusinessOfficeMasterData): void {
    this.$log.debug('MST_SALES_OFC > row=', row)
    const area = this.areaAllocationMasterData.find(element => element.id === row.areaId)
    if (!area || !area.prefectureList || area.prefectureList.length === 0) {
      row.prefectureId = ''
      this.$set(row, 'prefectureId', '')
      return
    }
    this.$set(row, 'prefectureId', area.prefectureList[0].id)
  }

  /**
   * 都道府県リストを取得する処理
   *
   * @param {string} areaId エリアID
   * @returns {void}
   */
  getPrefecturesByAreaId(areaId: string): PrefectureMasterData[] {
    const area = this.areaAllocationMasterData.find(element => element.id === areaId)
    if (!area || !area.prefectureList) {
      return []
    }
    return area.prefectureList
  }

  /**
   * 全都道府県リストを取得する処理
   *
   * @param {AreaMasterData[]} areaAllocationMasterData エリアリスト
   * @returns {PrefectureMasterData[]} 都道府県リスト
   */
  getPrefectureList(areaAllocationMasterData: AreaMasterData[]): PrefectureMasterData[] {
    const prefectureMap = {} as Record<string, PrefectureMasterData>
    areaAllocationMasterData.forEach(item => {
      if (!item.prefectureList) {
        return
      }

      item.prefectureList.forEach(prefecture => {
        if (prefectureMap[prefecture.id]) {
          return
        }

        prefectureMap[prefecture.id] = prefecture
      })
    })

    return Object.values(prefectureMap)
  }

  /**
   * コピー前の処理。
   *
   * @param {Array<any>} multipleSelection コピー対象
   * @returns {void}
   */
  // eslint-disable-next-line
  beforeDataCopy(multipleSelection: Array<any>): void {
    multipleSelection.forEach(item => {
      delete item.parentId
      delete item.hasUser
      delete item.hasBusinessOffice
    })
  }

  /**
   * 削除ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  async onDeleteButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onDeleteButtonClick`)
    if (!this.multipleSelection || this.multipleSelection.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataSelected').toString())
      return
    }

    this.doDeleteTableData()
  }

  /**
   * 削除チェックを行う。
   *
   * @returns {void}
   */
  // eslint-disable-next-line max-lines-per-function
  doDeleteTableData(): void {
    let hasUser = false
    let hasManagementTarget = false
    let canDelete = true

    const confirmCallback = async () => {
      this.doDelete()
    }

    for (const row of this.multipleSelection as BusinessOfficeMasterData[]) {
      // 削除対象に本社がある場合、下記のメッセージをポップアップする。
      if (row.id === localService.userInfo?.baseBusinessOfficeId) {
        msgBoxService.info(i18n.t('messages.masterCommon.E00088', [row.businessOfficeCode]).toString())
        canDelete = false
        break
      }

      // 削除対象に所属営業所がある場合、下記のメッセージをポップアップする。
      if (row.id === localService.userInfo?.businessOfficeId) {
        msgBoxService.info(i18n.t('messages.masterCommon.E00097', [row.businessOfficeCode]).toString())
        canDelete = false
        break
      }

      // 営業所が他営業所の上位営業所である場合、下記のメッセージをポップアップする。
      if (row.hasBusinessOffice) {
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [
              row.businessOfficeCode,
              i18n.t('master.salesOfficeMaster.hasBusinessOffice').toString()
            ])
            .toString()
        )
        canDelete = false
        break
      }

      // お気に入り有がの場合、下記のメッセージをポップアップする。
      if (row.hasBookmark) {
        msgBoxService.confirm(
          i18n.t('messages.masterCommon.E00077', [row.businessOfficeCode, row.userIdList]).toString(),
          confirmCallback
        )
        canDelete = false
        break
      }

      // 管理場所有がの場合、下記のメッセージをポップアップする。
      if (row.hasManagementPlace) {
        msgBoxService.confirm(
          i18n
            .t('messages.masterCommon.businessCanDelete', [
              row.businessOfficeCode,
              i18n.t('master.salesOfficeMaster.hasManagementPlace').toString()
            ])
            .toString(),
          confirmCallback
        )
        canDelete = false
        break
      }

      // 管理場所詳細有がの場合、下記のメッセージをポップアップする。
      if (row.hasManagementPlaceDtl) {
        msgBoxService.confirm(
          i18n
            .t('messages.masterCommon.businessCanDelete', [
              row.businessOfficeCode,
              i18n.t('master.salesOfficeMaster.hasManagementPlaceDtl').toString()
            ])
            .toString(),
          confirmCallback
        )
        canDelete = false
        break
      }

      hasUser = hasUser || (row.hasUser as boolean)
      hasManagementTarget = hasManagementTarget || (row.hasManagementTarget as boolean)
    }

    if (!canDelete) {
      return
    }

    // 所属ユーザ有無と管理対象有無がfalse場合、下記のメッセージをポップアップする。
    if (!hasUser && !hasManagementTarget) {
      msgBoxService.confirm(i18n.t('messages.masterCommon.deleteConfirm').toString(), confirmCallback)
      return
    }

    // 所属ユーザ有無と管理対象有無がtrueの場合、下記のメッセージをポップアップする。
    if (hasUser && hasManagementTarget) {
      msgBoxService.confirm(
        i18n
          .t('messages.masterCommon.I00150', [i18n.t('master.salesOfficeMaster.userManagementObject').toString()])
          .toString(),
        confirmCallback
      )
      return
    }

    // 所属ユーザ有無がtrueまたは管理対象有無がtrueの場合、下記のメッセージをポップアップする。
    if (hasUser && !hasManagementTarget) {
      msgBoxService.confirm(
        i18n.t('messages.masterCommon.I00150', [i18n.t('master.salesOfficeMaster.user').toString()]).toString(),
        confirmCallback
      )
      return
    } else {
      msgBoxService.confirm(
        i18n
          .t('messages.masterCommon.I00150', [i18n.t('master.salesOfficeMaster.managementTarget').toString()])
          .toString(),
        confirmCallback
      )
      return
    }
  }

  /**
   * 地図ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onMapButtonClick(): void {
    this.$log.debug(`MST_SALES_OFC > onMapButtonClick`)
    dialog.setLocationInfoShow(true)
  }

  /**
   * 地図で地域を選択した時の処理。
   *
   * @param {string} clickedMapAddress 地図で選択した住所
   * @returns {void}
   */
  onGetAdress(clickedMapAddress: string): void {
    if (!this.singleSelection) {
      return
    }

    this.$set(this.singleSelection, 'location', clickedMapAddress)
    this.locationRevalidate(this.formRef, 'location')
  }

  /**
   * 追加ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onInsertButtonClick(): void {
    this.$log.debug(`MST_SALES_OFC > onInsertButtonClick`)
    if (!this.checkInsert()) {
      return
    }

    let areaId = ''
    let prefectureId = ''

    if (this.areaAllocationMasterData && this.areaAllocationMasterData.length != 0) {
      areaId = this.areaAllocationMasterData[0].id
      if (
        this.areaAllocationMasterData[0].prefectureList &&
        this.areaAllocationMasterData[0].prefectureList.length != 0
      ) {
        prefectureId = this.areaAllocationMasterData[0].prefectureList[0].id
      }
    }

    const item = {
      ...this.formTemplate,
      dataType: DataType.INSERT,
      areaId: areaId,
      prefectureId: prefectureId,
      index: this.tableData.length
    }
    this.tableData.push(item)
    this.visableTableData.push(item)
    this.clearValidate()
    this.insertAfter()
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    return this.dataValidate()
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = this.getUpsertRequest()
    await updateBusinessOfficeMasterData(params)
    common.makeBusinessOfficeChange()
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    const res = await businessOfficeMasterCSVDownload()
    HelperService.download(res.fileName, res.data)
  }

  /**
   * CSVデータ合併前の処理
   *
   * @param {any[]} dataList データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  csvDataMergeIntoTableDataBefore(dataList: any[]): void {
    dataList.forEach(item => {
      item.dataType = DataType.INSERT
      if (!item.area) {
        return
      }

      const area = find(this.areaAllocationMasterData, { areaName: item.area })
      if (!area) {
        return
      }
      item.areaId = area.id

      if (!item.prefectures) {
        return
      }

      const prefectureList = area.prefectureList
      if (!prefectureList || prefectureList.length === 0) {
        return
      }

      const prefecture = find(prefectureList, { prefectureName: item.prefectures })
      if (!prefecture) {
        return
      }

      item.prefectureId = prefecture.id
    })
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      businessOfficeCode: '',
      businessOfficeName: '',
      area: '',
      prefectures: '',
      location: '',
      tel: '',
      mail: ''
    }
  }

  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'businessOfficeCode'
      },
      {
        type: FormItemType.Input,
        field: 'businessOfficeName'
      },
      {
        type: FormItemType.Select,
        field: 'areaId',
        list: () => {
          return this.areaAllocationMasterData.map(item => {
            return { code: item.id, name: item.areaName }
          })
        }
      },
      {
        type: FormItemType.Select,
        field: 'prefectureId',
        list: () => {
          return this.getPrefectureList(this.areaAllocationMasterData).map(item => {
            return { code: item.id, name: item.prefectureName }
          })
        }
      },
      {
        type: FormItemType.Input,
        field: 'location'
      },
      {
        type: FormItemType.Input,
        field: 'tel'
      },
      {
        type: FormItemType.Input,
        field: 'mail'
      }
    ]
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-viewTitle {
  margin-right: 20px;
}

.fileInput {
  display: none;
}
</style>
